import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { MultiTenancyModeEnum } from '@zaoblako/shared/modules/system/enums/multi-tenancy-mode.enum';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { ProfileService } from '@zaoblako/web/core/services/security/profile.service';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { MenuItem } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { ApiUrlPipe } from '@zaoblako/web/shared/pipes/api-url.pipe';
import { FileUrlPipe } from '@zaoblako/web/shared/pipes/file-url.pipe';
import { TooltipModule } from 'primeng/tooltip';
import { FrmAnimatedLogoComponent } from '@zaoblako/web/shared/components/frm-animated-logo/frm-animated-logo.component';
import { UserAvatarComponent } from '@zaoblako/web/shared/components/user-avatar/user-avatar.component';
import { TenantTypeEnum } from '@zaoblako/shared/modules/saas/enums/tenant.type.enum';
import type { AvailableTenantsInterface } from '@zaoblako/shared/modules/saas/dtos/available-tenants.dto';
import type { TenantFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-find.dto';
import type { ProfileFindInterface } from '@zaoblako/shared/modules/security/dtos/profile/profile-find.dto';

@Component({
  standalone: true,
  selector: 'crm-menu-component',
  templateUrl: 'crm-menu.component.html',
  styleUrls: ['./crm-menu.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    DialogModule,
    ButtonComponent,
    SpinnerComponent,
    TooltipModule,
    ApiUrlPipe,
    FileUrlPipe,
    FrmAnimatedLogoComponent,
    UserAvatarComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CrmMenuComponent implements OnInit, OnChanges {
  @Input() public minimized: boolean;

  /** DEV-only banner (same as CommonLayout devEnvironment). */
  @Input() public devEnvironment = false;

  @Output() public toggleMinimized = new EventEmitter<boolean>();

  public items: MenuItem[] = [];

  public currentProfile: ProfileFindInterface;
  public currentUser: UserFindInterface;

  public isShowTenantDialog = false;
  public isLoadingTenants = false;
  public tenants: AvailableTenantsInterface = {
    asInternalUser: [],
    asExternalUser: []
  };

  public constructor(
    private readonly authService: AuthService,
    public readonly globalService: GlobalService,
    private readonly profileService: ProfileService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit() {
    this.currentProfile = this.authService.currentProfile;
    this.currentUser = this.authService.currentUser;

    this.globalService
      .select((state) => state.tenantInfo)
      .subscribe((tenantInfo) => {
        this.items = [
          {
            label: 'ФРМ AI',
            icon: 'pi pi-sparkles',
            routerLink: '/ai/course',
            visible: this.globalService.isEnableModule('FRM_AI')
          },
          {
            label: 'Рабочий стол',
            icon: 'pi pi-desktop',
            routerLink: '/',
            routerLinkActiveOptions: { exact: true },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled
          },
          {
            label: 'Задачи',
            icon: 'pi pi-check-square',
            routerLink: '/project',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.PROJECT?.enabled
          },
          {
            label: 'Платежи',
            icon: 'pi pi-dollar',
            routerLink: '/payments',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.BRS?.enabled
          },
          // {
          //   label: 'Закупки',
          //   icon: 'pi pi-table',
          //   routerLink: '/purchase',
          //   routerLinkActiveOptions: { exact: false },
          //   visible: [TenantTypeEnum.ADMIN, TenantTypeEnum.DEMO].includes(tenantInfo?.type)
          // },
          {
            label: 'Бюджетирование',
            icon: 'pi pi-chart-bar',
            routerLink: '/budget',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.FINANCE?.enabled
          },
          {
            label: 'ОФД',
            icon: 'pi pi-book',
            routerLink: '/ofd',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.FDO?.enabled
          },
          {
            label: 'Документы',
            icon: 'pi pi-file',
            routerLink: '/document',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.FINANCE?.enabled
          },
          {
            label: 'Задолженности',
            icon: 'pi pi-info-circle',
            routerLink: '/debts',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.DEBTS?.enabled
          },
          {
            label: 'Сделки',
            icon: 'pi pi-briefcase',
            routerLink: '/deal',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.BUSINESS?.enabled
          },
          {
            label: 'Компании',
            icon: 'pi pi-building',
            routerLink: '/company',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled
          },
          {
            label: 'Клиенты',
            icon: 'pi pi-id-card',
            routerLink: '/client',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled
          },
          {
            label: 'Пользователи',
            icon: 'pi pi-users',
            routerLink: '/user',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled
          },
          {
            label: 'Справочники',
            icon: 'pi pi-list',
            routerLink: '/directory',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled
          },
          {
            label: 'HelpDesk',
            icon: 'pi pi-question-circle',
            routerLink: '/helpdesk',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.HELPDESK?.enabled
          },
          {
            label: 'Пропуска',
            icon: 'pi pi-key',
            routerLink: '/control',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.PHYSICAL_ACCESS_CONTROL?.enabled
          },
          {
            label: 'Паркинг',
            icon: 'pi pi-car',
            routerLink: '/passage',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.PHYSICAL_ACCESS_CONTROL?.enabled
          },
          {
            label: 'Трафик',
            icon: 'pi pi-sort-alt',
            routerLink: '/traffic',
            routerLinkActiveOptions: { exact: false },
            visible: tenantInfo?.ratePlan?.modulesAccessControl?.TRAFFIC?.enabled
          },
          {
            label: 'Админ панель',
            icon: 'pi pi-cog',
            routerLink: '/admin',
            routerLinkActiveOptions: { exact: false },
            visible: this.authService.currentProfile?.isSuperAdmin && tenantInfo?.type === TenantTypeEnum.ADMIN
          }
        ];
        this.updateTooltipMenuItem();
        this.cdRef.markForCheck();
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.minimized) {
      this.updateTooltipMenuItem();
    }
  }

  private updateTooltipMenuItem() {
    if (this.minimized && this.items?.length) {
      this.items = this.items.map((item) => ({
        ...item,
        tooltipOptions: {
          tooltipLabel: item.label,
          tooltipPosition: 'right',
          tooltipStyleClass: 'menu-item-tooltip',
          showDelay: 200,
          life: 3000
        }
      }));
      this.cdRef.markForCheck();
    }
    if (!this.minimized && this.items?.length) {
      this.items = this.items.map((item) => ({ ...item, tooltipOptions: null }));
      this.cdRef.markForCheck();
    }
  }

  public logout() {
    this.authService.logout(location.pathname + location.search);
  }

  public openTenantDialog() {
    this.isShowTenantDialog = true;
    this.isLoadingTenants = true;
    this.cdRef.markForCheck();

    this.profileService
      .getAvailableTenants()
      .subscribe({
        next: (tenants) => {
          this.isLoadingTenants = false;
          this.tenants = tenants;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.isLoadingTenants = false;
          this.cdRef.markForCheck();
        }
      });
  }

  public redirectToTenant(tenant: TenantFindInterface, isLk: boolean) {
    const subdomain = isLk
      ? tenant.personalAccountSubdomain
      : tenant.subdomain;
    window.open(`https://${subdomain}/authentication?token=${localStorage.getItem('JWT_TOKEN')}`, '_blank');
  }

  public get MultiTenancyModeEnum() {
    return MultiTenancyModeEnum;
  }
}
